import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { "Content-Type": "application/json" },
});

export function handleApiError(error: unknown) {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message;

      switch (status) {
        case 400:
          throw new Error(message || "Bad request.");
        case 401:
          throw new Error("Unauthorized.");
        case 403:
          throw new Error("Permission Denied.");
        case 404:
          throw new Error("Resource not found.");
        case 500:
          throw new Error("Internal server error. Please try again later.");
        default:
          throw new Error(message || "Unexpected error occurred.");
      }
    } else if (error.request) {
      throw new Error("No response from server. Check your connection.");
    } else {
      throw new Error("Request setup failed.");
    }
  }
}

export interface FetchAccessTokenRequest {
  email: string;
  password: string;
}

export async function fetchAccessToken(
  req: FetchAccessTokenRequest,
  abortSignal?: AbortSignal
) {
  const { data } = await api.post("/auth/sign-in", req, {
    signal: abortSignal,
  });

  return data as { accessToken: string };
}

export interface FetchAccessTokenWithGoogleRequest {
  accessToken: string;
}

export async function fetchAccessTokenWithGoogle(
  req: FetchAccessTokenWithGoogleRequest,
  abortSignal?: AbortSignal
) {
  const { data } = await api.post("/auth/google", req, {
    signal: abortSignal,
  });

  return data;
}

export type SignUpRequest = {
  email: string;
  password: string;
  countryCode: string;
  firstName: string;
  lastName: string;
  slug: string;
};

export async function signup(req: SignUpRequest, abortSignal?: AbortSignal) {
  const { data } = await api.post("/auth/register", req, {
    signal: abortSignal,
  });

  const message = data.message;
  if (typeof message === "string") {
    if (/^\[already_exists\]/.test(message))
      throw new Error(
        "An account with this email address already exists. Please use a different email or sign in if you already have an account."
      );
    throw new Error(message);
  }
  return data;
}

export interface ResetPasswordRequest {
  email: string;
}

export async function resetPassword(
  req: ResetPasswordRequest,
  abortSignal?: AbortSignal
) {
  const { data } = await api.post("/auth/reset-password", req, {
    signal: abortSignal,
  });

  return data;
}
